import { UserPreferenceKey, type UserPreferences } from '../types';
import { resolveGeographyPreference } from './geography-preference';
import {
  resolveWorkforceSegment,
  workforceSegmentToLegacyBoolean,
} from './workforce-segment';

/**
 * Flat, low-cardinality value props for the `preference changed` event.
 * DefaultFilters and PageOrder carry user content (filter trees, page ids),
 * so only their sizes are reported.
 */
export function trackablePreferenceValues(
  changedKeys: string[],
  prefs: Partial<UserPreferences>
): Record<string, string | number | boolean> {
  const values: Record<string, string | number | boolean> = {};

  for (const key of changedKeys) {
    switch (key) {
      case UserPreferenceKey.ContingentWorkers: {
        const value = prefs[key];
        if (value === undefined) break;
        const segment = resolveWorkforceSegment(value);
        values.value_contingent_workers =
          workforceSegmentToLegacyBoolean(segment);
        values.value_workforce_segment = segment;
        break;
      }
      case UserPreferenceKey.DataAdjustments: {
        const value = prefs[key];
        if (typeof value === 'boolean') values.value_data_adjustments = value;
        break;
      }
      case UserPreferenceKey.Geography: {
        // Was the 'msa'/'administrative' mode string; now the taxonomy id.
        // Goes through the normaliser because a pre-migration row is still a
        // bare string at runtime — reading `.taxonomyId` off it would track
        // the literal "undefined". Stringified so the Mixpanel property stays
        // single-typed across the change.
        const value = prefs[key];
        if (value) {
          values.value_geography = String(
            resolveGeographyPreference(value).taxonomyId
          );
        }
        break;
      }
      case UserPreferenceKey.ColorMode: {
        const value = prefs[key];
        if (value) values.value_color_mode = value;
        break;
      }
      case UserPreferenceKey.EconomicImpactIndustries: {
        const value = prefs[key];
        if (value) values.value_economic_impact_industries = value;
        break;
      }
      case UserPreferenceKey.EconomicImpactGeography: {
        const value = prefs[key];
        if (value) values.value_economic_impact_geography = value;
        break;
      }
      case UserPreferenceKey.Currency: {
        const code = prefs[key]?.code;
        if (code) values.value_currency = code;
        break;
      }
      case UserPreferenceKey.Taxonomies: {
        const taxonomies = prefs[key];
        if (!taxonomies) break;
        if (taxonomies.role === 'adaptive') {
          values.value_role_taxonomy = 'adaptive';
        } else if (taxonomies.role !== undefined) {
          values.value_role_taxonomy = taxonomies.role.default
            ? 'default'
            : 'custom';
        }
        if (taxonomies.skill !== undefined) {
          values.value_skill_taxonomy = String(taxonomies.skill);
        }
        if (taxonomies.activity !== undefined) {
          values.value_activity_taxonomy = String(taxonomies.activity);
        }
        break;
      }
      case UserPreferenceKey.DefaultFilters: {
        values.default_filters_count = prefs[key]?.length ?? 0;
        break;
      }
      case UserPreferenceKey.PageOrder: {
        values.page_order_count = prefs[key]?.length ?? 0;
        break;
      }
    }
  }

  return values;
}
